import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, Play, FlaskConical, RefreshCw } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { YithMappingPanel } from "@/components/admin/wp-import/YithMappingPanel";
import { RollbackDialog } from "@/components/admin/wp-import/RollbackDialog";

type Entity = "customers" | "posts" | "products" | "orders" | "bookings";

type JobRow = {
  id: string;
  status: string;
  dry_run: boolean;
  entities: string[];
  wp_url: string;
  counts: Record<string, { fetched: number; created: number; skipped: number; failed: number }>;
  log: Array<{ ts: string; level: string; msg: string }>;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  created_at: string;
};

const ENTITY_LABELS: Record<Entity, string> = {
  customers: "Users / Customers",
  posts: "Blog posts",
  products: "Products + variants",
  orders: "WooCommerce orders",
  bookings: "YITH bookings",
};

export default function AdminWpImport() {
  const [wpUrl, setWpUrl] = useState("");
  const [wpUser, setWpUser] = useState("");
  const [wpPass, setWpPass] = useState("");
  const [wcKey, setWcKey] = useState("");
  const [wcSecret, setWcSecret] = useState("");
  const [inviteUsers, setInviteUsers] = useState(true);
  const [dryRun, setDryRun] = useState(true);
  const [entities, setEntities] = useState<Record<Entity, boolean>>({
    customers: true, posts: true, products: true, orders: true, bookings: true,
  });

  const [running, setRunning] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);
  const [jobs, setJobs] = useState<JobRow[]>([]);
  const [studioMap, setStudioMap] = useState<Record<string, string>>({});
  const [scannedYithNames, setScannedYithNames] = useState<string[]>([]);

  const selectedEntities = useMemo(
    () => (Object.entries(entities) as [Entity, boolean][]).filter(([, v]) => v).map(([k]) => k),
    [entities],
  );

  const loadJobs = async () => {
    const { data } = await supabase
      .from("wp_import_jobs" as any)
      .select("*")
      .order("created_at", { ascending: false })
      .limit(10);
    setJobs((data ?? []) as unknown as JobRow[]);
  };

  useEffect(() => {
    loadJobs();
    const ch = supabase
      .channel("wp_import_jobs")
      .on("postgres_changes", { event: "*", schema: "public", table: "wp_import_jobs" }, () => loadJobs())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const runImport = async () => {
    if (!wpUrl || !wpUser || !wpPass) {
      toast.error("WP URL, username and application password are required");
      return;
    }
    if ((entities.products || entities.orders || entities.bookings) && (!wcKey || !wcSecret)) {
      toast.error("WooCommerce consumer key + secret required for products / orders / bookings");
      return;
    }
    if (selectedEntities.length === 0) {
      toast.error("Pick at least one entity to import");
      return;
    }

    // YITH mapping validation: if bookings are selected and YITH was scanned,
    // every scanned product must be mapped (or the user must add studios first).
    if (entities.bookings && !dryRun && scannedYithNames.length > 0) {
      const unmapped = scannedYithNames.filter((n) => !studioMap[n.toLowerCase().trim()]);
      if (unmapped.length > 0) {
        toast.error(
          `${unmapped.length} YITH product(s) have no studio mapping. Add studios at /admin/studios first, then map each product.`,
        );
        return;
      }
    }

    setRunning(true);
    try {
      const { data, error } = await supabase.functions.invoke("wp-import", {
        body: {
          mode: "import",
          wp_url: wpUrl,
          wp_username: wpUser,
          wp_app_password: wpPass,
          wc_consumer_key: wcKey,
          wc_consumer_secret: wcSecret,
          entities: selectedEntities,
          dry_run: dryRun,
          invite_users: inviteUsers,
          studio_map: studioMap,
        },
      });
      if (error) throw error;
      setActiveJobId(data?.job_id ?? null);
      toast.success(
        dryRun
          ? "Dry run started — progress will update live below"
          : "Import started — progress will update live below",
      );
      loadJobs();
    } catch (e) {
      toast.error((e as Error).message);
    } finally {
      setRunning(false);
    }
  };

  const activeJob = jobs.find((j) => j.id === activeJobId) ?? jobs[0];
  const hasRunningJob = jobs.some((j) => j.status === "running");

  useEffect(() => {
    if (!hasRunningJob) return;

    const intervalId = window.setInterval(() => {
      loadJobs();
    }, 3000);

    return () => window.clearInterval(intervalId);
  }, [hasRunningJob]);

  return (
    <div className="pt-16 min-h-screen">
      <div className="p-6 lg:p-10 max-w-5xl mx-auto">
        <Link to="/admin" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 text-sm">
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </Link>
        <h1 className="text-3xl font-display tracking-widest mb-2">Import from WordPress</h1>
        <p className="text-muted-foreground mb-8 text-sm max-w-2xl">
          One-time migration from a self-hosted WordPress + WooCommerce + YITH Booking site.
          Idempotent — safe to re-run.
        </p>

        <Card className="p-4 mb-6 bg-surface border-border flex items-center justify-between gap-4">
          <div className="text-sm">
            <div className="font-semibold uppercase tracking-wider">Orders + Bookings stuck?</div>
            <div className="text-muted-foreground">
              Live pulls time out at 150s. Upload a WC orders JSON export and it'll batch-import without timeouts.
            </div>
          </div>
          <Link to="/admin/wp-import/upload">
            <Button variant="outline">Upload JSON →</Button>
          </Link>
        </Card>

        <Card className="p-6 space-y-6 mb-8 bg-surface border-border">
          <div>
            <Label className="text-xs uppercase tracking-wider mb-2 block">WordPress site</Label>
            <Input
              placeholder="https://your-wp-site.com"
              value={wpUrl}
              onChange={(e) => setWpUrl(e.target.value)}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">WP username</Label>
              <Input value={wpUser} onChange={(e) => setWpUser(e.target.value)} placeholder="admin" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">WP application password</Label>
              <Input
                type="password"
                value={wpPass}
                onChange={(e) => setWpPass(e.target.value)}
                placeholder="xxxx xxxx xxxx xxxx"
              />
              <p className="text-xs text-muted-foreground mt-1">
                Generate at WP admin → Users → Profile → Application Passwords.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t border-border">
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">WooCommerce consumer key</Label>
              <Input value={wcKey} onChange={(e) => setWcKey(e.target.value)} placeholder="ck_..." />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider mb-2 block">WooCommerce consumer secret</Label>
              <Input
                type="password"
                value={wcSecret}
                onChange={(e) => setWcSecret(e.target.value)}
                placeholder="cs_..."
              />
              <p className="text-xs text-muted-foreground mt-1">
                WooCommerce → Settings → Advanced → REST API. Read permissions are enough.
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-border">
            <Label className="text-xs uppercase tracking-wider mb-3 block">What to import</Label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {(Object.keys(ENTITY_LABELS) as Entity[]).map((e) => (
                <label key={e} className="flex items-center gap-2 cursor-pointer">
                  <Checkbox
                    checked={entities[e]}
                    onCheckedChange={(v) => setEntities({ ...entities, [e]: !!v })}
                  />
                  <span className="text-sm">{ENTITY_LABELS[e]}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-6 pt-4 border-t border-border">
            <label className="flex items-center gap-3">
              <Switch checked={dryRun} onCheckedChange={setDryRun} />
              <span className="text-sm">Dry run (count only, no writes)</span>
            </label>
            <label className="flex items-center gap-3">
              <Switch checked={inviteUsers} onCheckedChange={setInviteUsers} />
              <span className="text-sm">Create auth accounts + invite emails</span>
            </label>
          </div>

          <div className="flex gap-3 pt-4">
            <Button onClick={runImport} disabled={running} className="gap-2">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : dryRun ? <FlaskConical className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {dryRun ? "Run dry-run preview" : "Start import"}
            </Button>
            <Button variant="outline" onClick={loadJobs} className="gap-2">
              <RefreshCw className="w-4 h-4" /> Refresh
            </Button>
          </div>
        </Card>

        {entities.bookings && (
          <div className="mb-8">
            <YithMappingPanel
              creds={{
                wp_url: wpUrl,
                wc_consumer_key: wcKey,
                wc_consumer_secret: wcSecret,
              }}
              studioMap={studioMap}
              onMapChange={setStudioMap}
              onItemsScanned={setScannedYithNames}
            />
            {Object.keys(studioMap).length > 0 && (
              <p className="text-xs text-muted-foreground mt-2">
                {Object.keys(studioMap).length} mapping(s) will override auto-match.
              </p>
            )}
          </div>
        )}

        {activeJob && (
          <Card className="p-6 bg-surface border-border mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-display tracking-wider">Latest job</h2>
              <div className="flex items-center gap-3">
                <Badge variant={
                  activeJob.status === "succeeded" ? "default" :
                  activeJob.status === "running" ? "secondary" :
                  activeJob.status === "partial" ? "outline" : "destructive"
                }>
                  {activeJob.status}{activeJob.dry_run ? " · dry-run" : ""}
                </Badge>
                {!activeJob.dry_run && activeJob.status !== "running" && (
                  <RollbackDialog
                    jobId={activeJob.id}
                    countsByEntity={{
                      customer: activeJob.counts?.customers?.created ?? 0,
                      post: activeJob.counts?.posts?.created ?? 0,
                      product: activeJob.counts?.products?.created ?? 0,
                      order: activeJob.counts?.orders?.created ?? 0,
                      booking: activeJob.counts?.bookings?.created ?? 0,
                    }}
                    onDone={loadJobs}
                  />
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              {(Object.keys(ENTITY_LABELS) as Entity[]).map((e) => {
                const c = activeJob.counts?.[e];
                return (
                  <div key={e} className="border border-border p-3">
                    <div className="text-xs uppercase tracking-wider text-muted-foreground">{ENTITY_LABELS[e]}</div>
                    {c ? (
                      <div className="mt-1 text-sm">
                        <div><span className="text-muted-foreground">fetched</span> <span className="font-mono">{c.fetched}</span></div>
                        <div><span className="text-primary">created</span> <span className="font-mono">{c.created}</span></div>
                        <div><span className="text-muted-foreground">skipped</span> <span className="font-mono">{c.skipped}</span></div>
                        {c.failed > 0 && <div><span className="text-destructive">failed</span> <span className="font-mono">{c.failed}</span></div>}
                      </div>
                    ) : <div className="mt-1 text-sm text-muted-foreground">—</div>}
                  </div>
                );
              })}
            </div>
            <div className="border border-border bg-background p-3 max-h-80 overflow-auto font-mono text-xs">
              {(activeJob.log ?? []).slice().reverse().map((l, i) => (
                <div key={i} className={
                  l.level === "error" ? "text-destructive" :
                  l.level === "warn" ? "text-yellow-500" : "text-muted-foreground"
                }>
                  [{l.level}] {l.msg}
                </div>
              ))}
              {(activeJob.log ?? []).length === 0 && <div className="text-muted-foreground">No logs yet…</div>}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}